import _ from 'underscore';
import dayjs from '../../../../shared/services/dayjs.js';
import systemNachrichtService from './systemNachrichtService.js';
import { debounce, fuelleSelectOptionen } from '../util.js';
import SystemDialogService from '../../../../shared/services/systemDialogService.js';
import stammdatenService from './stammdatenService.js';
import { PROTOKOLL_MARKUP, setupProtokoll } from './neoCheckerProtokoll.js';

export default class NeoChecker {
  constructor() {
    this.deleteMode = false;
    this.mitarbeiterID = false;
    // Create widget container
    this.todo = document.createElement('div');
    // minimized start
    this.todo.className = 'todo-widget minimized';
    this.todo.innerHTML = `
      <div class="todo-header">
        <span>neoChecker</span><span class="checklist-title"></span>
        <span class="neochecker-count zero" title="Offene Todos">0</span>
        <button class="settings" style="margin-left: auto;"><i class="bi bi-pencil-square"></i></button>
        <button class="minimize"><i class="bi bi-fullscreen"></i></button>
      </div>
      <div class="todo-toolbar">
        <button disabled class="import-btn" title="Checkliste aus Vorlage importieren">Import</button>
        <button disabled class="reset-btn" title="Alle Haken wieder herausnehmen">Reset</button>
        <button disabled class="delete-btn" title="Löschmodus aktivieren">Löschen</button>
      </div>
      <div class="neochecker-messages"></div>

      <div class="todo-add">
        <input type="text" placeholder="Neues Todo..." disabled>
        <button class="add-btn" disabled><i class="bi bi-plus-lg"></i></button>
      </div>
      <div class="todo-body">
        <ul></ul>
        <div id="neochecker-idle" style="text-align: center; padding: 10px;">Warte auf Mitarbeiterauswahl...</div>
      </div>
      ${PROTOKOLL_MARKUP}
    `;

    document.body.appendChild(this.todo);
    this.protokoll = setupProtokoll(this.todo);

    this.ul = this.todo.querySelector('ul');
    this.minimizeBtn = this.todo.querySelector('.minimize');
    this.resetBtn = this.todo.querySelector('.reset-btn');
    this.deleteBtn = this.todo.querySelector('.delete-btn');
    this.settingsBtn = this.todo.querySelector('.settings');
    this.addBtn = this.todo.querySelector('.add-btn');
    this.addInput = this.todo.querySelector('.todo-add input');
    this.importBtn = this.todo.querySelector('.import-btn');
    this.messages = this.todo.querySelector('.neochecker-messages');
    this.countBadge = this.todo.querySelector('.neochecker-count');

    this._setupDrag();
    this._setupMinimize();
    this._setupToolbar();
    this._setupAddTask();

    
    this._debounceSaveToStorage = debounce(async () => this._saveToStorage(), 1000);
    

    // Avoid outside of window view
    window.addEventListener('resize', () => {
      if (this.todo) this._restrictToViewport(this.todo);
    });
  }

  // Public
  async loadEmployeeChecklist(mitarbeiterID, mitarbeiterName) {
      // Meldungen sofort leeren, damit parallel angezeigte Fristen-Meldungen des neuen MA nicht nach dem Speichern gelöscht werden
      this.hideMessages();
      // nur force update, wenn es auch Änderungen gab.
      if (window.myVars.neocheckerDebounceId > 0 && !_.isEmpty(this.mitarbeiterID) && !_.isEqual(this.mitarbeiterID, mitarbeiterID)) {
        // force update last list before loading new ma
        await this._saveToStorage();
        clearTimeout(window.myVars.neocheckerDebounceId);
      }
      // debounceId zurücksetzen
      window.myVars.neocheckerDebounceId = null;
      this.mitarbeiterID = mitarbeiterID;
      this.todo.querySelector('.checklist-title').innerText = mitarbeiterName ? mitarbeiterName : '';
      this.ul.innerText = '';
      this._aktualisiereOffeneAnzahl();
      await this._loadFromStorage();
      await this._loadProtokoll(mitarbeiterID);
  }

  // lädt das Änderungsprotokoll des aktuell angezeigten Mitarbeiters neu (z.B. nach dem Speichern)
  async reloadProtokoll() {
    if (this.mitarbeiterID) await this._loadProtokoll(this.mitarbeiterID);
  }

  // zeigt das Änderungsprotokoll des Unternehmens (Verwaltungsbereich)
  // und setzt dabei die Mitarbeiterauswahl zurück
  async loadUnternehmenProtokoll() {
    await this.loadEmployeeChecklist({}, '– Verwaltung');
    await this._loadUnternehmenProtokoll();
  }

  // lädt das Unternehmensprotokoll neu (z.B. nach dem Speichern),
  // aber nur solange kein Mitarbeiter ausgewählt ist
  async reloadUnternehmenProtokoll() {
    if (_.isEmpty(this.mitarbeiterID)) await this._loadUnternehmenProtokoll();
  }

  async _loadUnternehmenProtokoll() {
    try {
      const res = await fetch('/neolohn/api/protokoll/unternehmen');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const docs = await res.json();
      // verwerfen, falls inzwischen doch ein Mitarbeiter ausgewählt wurde
      if (!_.isEmpty(this.mitarbeiterID)) return;
      this.protokoll.render(docs);
    } catch (err) {
      console.log(err);
      if (!_.isEmpty(this.mitarbeiterID)) return;
      this.protokoll.render([]);
    }
  }

  /**
   * Zeigt eine Meldung im neoChecker an.
   * @param {string} text
   * @param {string} type info / warning / error
   * @param {boolean} zaehlen Meldung als offenes Todo im Header mitzählen (Standard: nur Fehler)
   */
  showMessage(text, type = 'info', zaehlen = type === 'error') {
    const msg = document.createElement('div');
    msg.className = `neochecker-message ${type}`;
    msg.classList.toggle('zaehlt', zaehlen);

    const span = document.createElement('span');
    span.textContent = text;

    const close = document.createElement('span');
    close.className = 'close';
    close.innerHTML = '<i class="bi bi-x-lg"></i>'; // ✖ symbol
    close.addEventListener('click', () => msg.remove());

    close.addEventListener('click', () => this._aktualisiereOffeneAnzahl());

    msg.appendChild(span);
    msg.appendChild(close);

    this.messages.appendChild(msg);
    this._aktualisiereOffeneAnzahl();
  }

  hideMessages() {
     this.messages.innerHTML = '';
     this._aktualisiereOffeneAnzahl();
  }

  // Private
  // zeigt die Anzahl der offenen (nicht abgehakten) Todos und gezählten Meldungen (z.B. fehlende Fristen) im Header an
  _aktualisiereOffeneAnzahl() {
    const offen = this.ul.querySelectorAll('input[type="checkbox"]:not(:checked)').length
      + this.messages.querySelectorAll('.neochecker-message.zaehlt').length;
    this.countBadge.innerText = offen;
    this.countBadge.classList.toggle('zero', offen === 0);
  }

  _addListElement(item, save = true) {
    this._unsetDeleteMode();
    const li = document.createElement('li');
    const timestamp = item?.timestamp ? item.timestamp : Date.now();
    li.dataset.timestamp = timestamp;
    li.dataset.editor = item?.editor;
    const initials = item?.editor ? item.editor.split(' ').reduce((acc, elem) => acc + (elem[0] ? elem[0] : ''), '') : '';
    li.innerHTML = `
      <input type="checkbox" ${item?.checked ? 'checked' : ''}>
      <span class="text">${item.text}</span>
      <div>
        <span class="timestamp">${dayjs(parseInt(timestamp)).tz().format('DD.MM.YY, HH:mm')}</span>
        <span class="editor" title="Zuletzt bearbeitet von: ${item?.editor}">${initials}</span>
      </div>
    `;
    li.classList.add('new-item');

    const checkbox = li.querySelector('input');
    checkbox.addEventListener('click', (e) => {
      e.stopPropagation();
      this._elementClickHandler(checkbox, li);
    });

    li.addEventListener('click', (e) => {
        e.stopPropagation();
      if (this.deleteMode) {
        this.ul.removeChild(li);
        this._aktualisiereOffeneAnzahl();
        this._debounceSaveToStorage('neochecker');
      } else {
        li.querySelector('input').checked = !li.querySelector('input').checked;
        this._elementClickHandler(checkbox, li);
      }
    });
    
    this.ul.insertAdjacentElement('afterbegin', li);
    this._sortAllTasks();
    this._aktualisiereOffeneAnzahl();

    // Fade-in animation
    requestAnimationFrame(() => {
      li.classList.remove('new-item');
    });

    if (save) this._debounceSaveToStorage('neochecker');
  }

  _elementClickHandler(checkbox, li) {
      const timestamp = Date.now();
      li.dataset.timestamp = timestamp;
      li.dataset.editor = window.myVars.Username;
      li.querySelector('.timestamp').innerText = dayjs(timestamp).tz().format('DD.MM.YY, HH:mm');
      const initials = li.dataset.editor ? li.dataset.editor.split(' ').reduce((acc, elem) => acc + (elem[0] ? elem[0] : ''), '') : '';
      li.querySelector('.editor').innerText = initials;
      this._sortTasks(checkbox, li);
      this._aktualisiereOffeneAnzahl();
      this._debounceSaveToStorage('neochecker');
  }

  _reset() {
    // Kein ausversehenes Reset von checkboxen wenn jemand den Löschmodus verlassen will
    if (this.deleteMode) {
      this._unsetDeleteMode();
      return;
    }
    this.ul.querySelectorAll('li').forEach(li => {
      const cb = li.querySelector('input[type="checkbox"]');
      cb.checked = false;
      const timestamp = Date.now();
      li.dataset.timestamp = timestamp;
      li.querySelector('.timestamp').innerText = dayjs(timestamp).tz().format('DD.MM.YY, HH:mm');
    });
    this._sortAllTasks();
    this._aktualisiereOffeneAnzahl();
    this._debounceSaveToStorage('neochecker');
    this._unsetDeleteMode();
  }

  _setupToolbar() {
    this.resetBtn.addEventListener('click', () => this._reset());
    this.deleteBtn.addEventListener('click', () => {
      this.deleteMode = !this.deleteMode;
      if (this.deleteMode) {
        this.todo.querySelector('.todo-body').classList.add('delete-mode');
        this.deleteBtn.style.background = 'var(--custom-red)';
        this.deleteBtn.style.color = 'white';
      } else {
        this._unsetDeleteMode();
      }
    });
    this.importBtn.addEventListener('click', () => this._importTemplate());
    this.settingsBtn.addEventListener('click', () => {
      resetDialog();
      SystemDialogService.instance.display('neochecker-template-dialog');
    });
  }

  _unsetDeleteMode() {
    this.deleteMode = false;
    this.todo.querySelector('.todo-body').classList.remove('delete-mode');
    this.deleteBtn.style.background = '';
    this.deleteBtn.style.color = '';
  }

  async _importTemplate() {
    // Let user choose
    const select = document.getElementById('neochecker-template-selection');
    fillTemplateSelect(select, false);
    const choice = await SystemDialogService.instance.displayAsync('neochecker-template-selection-dialog');
    if (choice.success && choice.data) {
      const templates = stammdatenService.unternehmensobjekt.NeoCheckerTemplates;
      const templateChoice = templates.find((tmp) => tmp._id === choice.data.templateID);
        if (templateChoice) {
           templateChoice.Tasks.forEach(item => this._addListElement({
            text: item.text,
            checked: false,
            timestamp: Date.now(),
            editor: window.myVars.Username,
          }));
        }
    }
  }

  _setupAddTask() {
    const addTask = () => {
      const text = this.addInput.value.trim();
      if (text) {
        const newItem = {
          text,
          checked: false,
          timestamp: Date.now(),
          editor: window.myVars.Username
        };
        this._addListElement(newItem, true);
        this.addInput.value = '';
      }
    };
    this.addBtn.addEventListener('click', addTask);
    this.addInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') addTask();
    });
  }

  _sortTasks(checkbox, li) {
    const listItems = Array.from(this.ul.children);
    // Für eine smoothe animation müssen wir uns die Koordinaten mappen
    const firstRects = new Map();
    listItems.forEach(item => firstRects.set(item, item.getBoundingClientRect()));

    // Reorder DOM
    if (checkbox.checked) {
        // Find position: before first checked item
        const firstChecked = Array.from(this.ul.children).find(
            child => child.querySelector('input').checked && child != li
        );
        firstChecked ? this.ul.insertBefore(li, firstChecked) : this.ul.appendChild(li);
    } else {
        this.ul.insertBefore(li, this.ul.firstChild);
    }

    // FLIP animation
    listItems.forEach(item => {
      const lastRect = item.getBoundingClientRect();
      const firstRect = firstRects.get(item);
      const deltaY = firstRect.top - lastRect.top;
      if (deltaY) {
        item.style.transform = `translateY(${deltaY}px)`;
        item.style.transition = 'transform 0s';
        requestAnimationFrame(() => {
          item.style.transform = '';
          item.style.transition = 'transform 0.3s ease';
        });
      }
    });
  }

  _sortAllTasks() {
    const items = Array.from(this.ul.children);
    items.sort((a, b) => {
      const aChecked = a.querySelector('input').checked;
      const bChecked = b.querySelector('input').checked;
      if (aChecked !== bChecked) return aChecked - bChecked; // unchecked fist
      return b.dataset.timestamp - a.dataset.timestamp; // newer second
    });
    items.forEach(item => this.ul.appendChild(item));
  }

  _setupDrag() {
    let isDragging = false;
    let offsetX = 0;
    let offsetY = 0;
    const header = this.todo.querySelector('.todo-header');
    // start drag
    header.addEventListener('mousedown', (e) => {
      e.preventDefault();
      isDragging = true;
      offsetX = e.clientX - this.todo.offsetLeft;
      offsetY = e.clientY - this.todo.offsetTop;
    });
    // while drag move positition
    document.addEventListener('mousemove', (e) => {
      if (isDragging) {
        this.todo.style.left = (e.clientX - offsetX) + 'px';
        this.todo.style.top = (e.clientY - offsetY) + 'px';
      }
    });
    // stop drag
    document.addEventListener('mouseup', () => isDragging = false);
  }

  _setupMinimize() {
    this.minimizeBtn.addEventListener('click', () => {
      this._saveMinimized();
    });
    // merken ob der User die Liste ausgeklappt hat oder nicht
    const storedState = localStorage.getItem('neoCheckerOpen');
    if (storedState === 'true') {
        this.todo.classList.remove('minimized'); // open
        this.minimizeBtn.innerHTML = '<i class="bi bi-fullscreen-exit"></i>';
    } else {
        this.todo.classList.add('minimized'); // closed
        this.minimizeBtn.innerHTML = '<i class="bi bi-fullscreen"></i>';
    }
  }

  _saveMinimized() {
    const isOpen = !this.todo.classList.contains('minimized');
    if (isOpen) {
        this.todo.classList.add('minimized');
        this.minimizeBtn.innerHTML = '<i class="bi bi-fullscreen"></i>';
    } else {
        this.todo.classList.remove('minimized');
        this.minimizeBtn.innerHTML = '<i class="bi bi-fullscreen-exit"></i>';
    }

    // Save state in localStorage
    localStorage.setItem('neoCheckerOpen', (!isOpen).toString());
  }

  // Storage
  async _saveToStorage() {
    try {
      const tasks = Array.from(this.ul.children).map(li => ({
        text: li.querySelector('span').textContent,
        checked: li.querySelector('input').checked,
        timestamp: parseFloat(li.dataset.timestamp),
        editor: li.dataset.editor
      }));
      const response = await fetch(`/neolohn/api/neochecker/mitarbeiter/${this.mitarbeiterID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({Tasks: tasks})
      });
      if (!response.ok) {
        const msg = await response.text();
        systemNachrichtService.zeigeKleineNachricht(msg);
        throw new Error(`HTTP error, status = ${response.status}`);
      }
      // debounceId zurücksetzen
      window.myVars.neocheckerDebounceId = null;
    } catch (error) {
      console.log(error);
      systemNachrichtService.zeigeKleineNachricht('Checkliste konnte nicht gespeichert werden!', -1);
    }
  }


  // (de)aktiviert die Checklisten-Bedienelemente. Header-Buttons (minimieren/
  // Einstellungen) und der Protokoll-Toggle bleiben immer bedienbar.
  _setControlsDisabled(disabled) {
    this.todo.querySelectorAll('input, button').forEach((elem) => {
      if (elem.closest('.todo-header') || elem.classList.contains('np-toggle')) return;
      elem.disabled = disabled;
    });
  }

  async _loadFromStorage() {
    // merken, für welchen Mitarbeiter geladen wird (Schutz vor veralteten Antworten)
    const mitarbeiterID = this.mitarbeiterID;
    try {
      if (_.isEmpty(mitarbeiterID)) {
        this._setControlsDisabled(true);
        this.todo.querySelector('#neochecker-idle').style.display = 'block';
        return;
      };
      const response = await fetch(`/neolohn/api/neochecker/mitarbeiter/${mitarbeiterID}`);
      // veraltete Antwort verwerfen, falls inzwischen ein anderer Mitarbeiter ausgewählt wurde
      if (mitarbeiterID !== this.mitarbeiterID) return;
      if (!response.ok) {
        const msg = await response.text();
        systemNachrichtService.zeigeKleineNachricht(msg);
        throw new Error(`HTTP error, status = ${response.status}`);
      }
      const tasks = await response.json();
      if (mitarbeiterID !== this.mitarbeiterID) return;
      if (tasks) {
        tasks.forEach(t => this._addListElement(t, false));
      }
      this._setControlsDisabled(false);
      this.todo.querySelector('#neochecker-idle').style.display = 'none';
    } catch (error) {
      if (mitarbeiterID !== this.mitarbeiterID) return;
      console.log(error);
      systemNachrichtService.zeigeKleineNachricht('Checkliste konnte nicht geladen werden!', -1);
    }
  }

  async _loadProtokoll(mitarbeiterID) {
    if (_.isEmpty(mitarbeiterID)) { this.protokoll.render([]); return; }
    try {
      const res = await fetch(`/neolohn/api/protokoll/mitarbeiter/${mitarbeiterID}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const docs = await res.json();   // Array von Protokoll-Dokumenten
      // veraltete Antwort verwerfen, falls inzwischen ein anderer Mitarbeiter ausgewählt wurde
      if (mitarbeiterID !== this.mitarbeiterID) return;
      this.protokoll.render(docs);
    } catch (err) {
      console.log(err);
      if (mitarbeiterID !== this.mitarbeiterID) return;
      this.protokoll.render([]); // leerer Zustand statt Fehler
    }
  }

  // Verhindert, dass der neolohn Checker nicht aus der Bildfläche verschwindet
  _restrictToViewport(element) {
    const rect = element.getBoundingClientRect();
    let x = rect.left;
    let y = rect.top;

    // clamp X
    if (x + rect.width > window.innerWidth) {
        x = window.innerWidth - rect.width;
    }
    if (x < 0) x = 0;

    // clamp Y
    if (y + rect.height > window.innerHeight) {
        y = window.innerHeight - rect.height;
    }
    if (y < 0) y = 0;

    element.style.left = x + 'px';
    element.style.top = y + 'px';
  }
}

window.myHandlers = window.myHandlers || {};
window.myHandlers.neoCheckerTemplatesAddTodo = neoCheckerTemplatesAddTodo;
window.myHandlers.neoCheckerTemplatesDelete = neoCheckerTemplatesDelete;
window.myHandlers.neoCheckerTemplatesSave = neoCheckerTemplatesSave;
window.myHandlers.neoCheckerTemplatesSelect = neoCheckerTemplatesSelect;
window.myHandlers.confirmNeoCheckerTemplateSelection = confirmNeoCheckerTemplateSelection;

function neoCheckerTemplatesAddTodo(value = '') {
  const todolist = document.getElementById('neochecker-template-todo-list');
  const tabIndex = [...document.body.querySelectorAll('.neochecker-template-todo')].length + 1;
  todolist.insertAdjacentHTML('beforeend', `<input class="neochecker-template-todo" type="text" placeholder="Todo eingeben..." tabindex="${tabIndex}" value="${value}">`);
  document.getElementById('neochecker-template-add-todo').tabIndex = tabIndex + 1;
}

function fillTemplateSelect(selectElement,  isEditor) {
  const unternehmen = stammdatenService.unternehmensobjekt;
  const vorlagen = unternehmen.NeoCheckerTemplates || [];
  if (vorlagen) {
    fuelleSelectOptionen(selectElement, vorlagen, '_id', 'Title', true, isEditor ? 'Neue Vorlage erstellen' : 'Bitte Vorlage auswählen', '');
  }
}

function neoCheckerTemplatesSelect(thisElement) {
  const templateID = thisElement.value;
  if (templateID) {
    const templates = stammdatenService.unternehmensobjekt.NeoCheckerTemplates;
    const template = templates.find((tmp) => tmp._id === templateID);
    if (!template) return resetDialog();
    // remove old todos
    [...document.body.querySelectorAll('.neochecker-template-todo')].forEach((elem) => elem.remove());
    // add selected title
    document.getElementById('neochecker-template-title').value = template.Title;
    // add new todos with values
    template.Tasks.forEach((task) => neoCheckerTemplatesAddTodo(task.text));
  } else {
    resetDialog();
  }
}

async function neoCheckerTemplatesSave() {
  try {
    const unternehmen = stammdatenService.unternehmensobjekt;
    unternehmen.NeoCheckerTemplates;
    const select = document.getElementById('neochecker-template-select');
    const templateID = select.value;
    // if empty we post a new template elsewise we put/update existing one
    const method = templateID == '' ? 'POST' : 'PUT';
    const template = {
      _id: templateID,
      Title: document.getElementById('neochecker-template-title').value,
      Tasks: []
    };
    [...document.body.querySelectorAll('.neochecker-template-todo')].forEach((elem) => {
      if (elem.value.trim()) {
        template.Tasks.push({text: elem.value});
      }
    });
    const response = await fetch('/neolohn/api/neochecker/templates', {
      method,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(template)
    });
    if (!response.ok) {
      const msg = await response.text();
      systemNachrichtService.zeigeKleineNachricht(msg);
      throw new Error(`HTTP Error Status: ${response.status}`);
    }
    systemNachrichtService.zeigeKleineNachricht('Vorlage gespeichert!', 1);
    // Unternehmen aktualisieren
    await stammdatenService.holeUnternehmensdaten(true);
    resetDialog();
  } catch (error) {
    console.log(error);
    systemNachrichtService.zeigeKleineNachricht('Vorlage konnte nicht gespeichert werden!', -1);
  }
}

async function neoCheckerTemplatesDelete() {
  try {
    const select = document.getElementById('neochecker-template-select');
    const templateID = select.value;
    if (!templateID) return systemNachrichtService.zeigeKleineNachricht('Ungespeicherte Vorlage kann nicht gelöscht werden');
    const response = await fetch(`/neolohn/api/neochecker/templates/${templateID}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const msg = await response.text();
      systemNachrichtService.zeigeKleineNachricht(msg);
      throw new Error(`HTTP Error Status: ${response.status}`);
    }
    systemNachrichtService.zeigeKleineNachricht('Vorlage entfernt!', 1);
    // Unternehmen aktualisieren
    await stammdatenService.holeUnternehmensdaten(true);
    resetDialog();
  } catch (error) {
    console.log(error);
    systemNachrichtService.zeigeKleineNachricht('Vorlage konnte nicht gelöscht werden!', -1);
  }
}

function resetDialog() {
  document.getElementById('neochecker-template-title').value = '';
  const select = document.getElementById('neochecker-template-select');
  fillTemplateSelect(select, true);
  [...document.body.querySelectorAll('.neochecker-template-todo')].forEach((elem) => elem.remove());
  neoCheckerTemplatesAddTodo();
  neoCheckerTemplatesAddTodo();
  neoCheckerTemplatesAddTodo();
}

function confirmNeoCheckerTemplateSelection() {
  const templateID = document.getElementById('neochecker-template-selection').value;
  if (!templateID) return;
  SystemDialogService.instance.confirm({templateID});
}